import { lazy, Suspense } from 'react'

import { createFileRoute, Outlet } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { actionQueries } from '@/features/actions/queries'
import { useCasePageContext } from '@/features/air/case-management/ai/hooks/use-case-page-context'
import {
	CaseDetailsSkeleton,
	DetailsHeaderClusterSkeleton,
	DetailsTabsSkeleton,
	DetailsTitleSkeleton,
} from '@/features/air/case-management/components/details/skeletons'
import { caseQueries } from '@/features/air/case-management/queries'
import { playbookQueries } from '@/features/playbooks/queries'

import { getCurrentTenant } from '@/shared/auth/store'

import '@/features/air/case-management/ai/register'

import { AIRailLayout } from '@/features/ai/components/panel/ai-rail-layout'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { Section } from '@/features/app-shell/section'

import { ErrorBoundary } from '@/components/common/error-boundary'

const DetailsTitle = lazy(
	() => import('@/features/air/case-management/components/details/title'),
)

const DetailsHeaderCluster = lazy(
	() =>
		import('@/features/air/case-management/components/details/header-cluster'),
)

const DetailsTabs = lazy(
	() => import('@/features/air/case-management/components/details/tabs'),
)

const LogPanelMount = lazy(
	() =>
		import('@/features/air/case-management/components/details/logs/log-panel-mount'),
)

const searchSchema = z.object({
	enrichKey: z.string().optional(),
	enrichValue: z.string().optional(),
	logId: z.string().optional(),
})

export const Route = createFileRoute('/_dashboard/air/case-management/$id')({
	validateSearch: zodValidator(searchSchema),
	loader: async ({ params, context: { queryClient } }) => {
		const { id } = params
		const tenant = getCurrentTenant() as string

		// Since we display Automation Status, we need to prefetch Actions and Playbooks
		if (tenant) {
			queryClient.prefetchQuery(actionQueries.list(tenant))
			queryClient.prefetchQuery(playbookQueries.list(tenant))

			const data = await queryClient.ensureQueryData({
				...caseQueries.case_details(tenant, id),
				revalidateIfStale: true,
			})

			// SKU-on (investigation present): warm the lazy-per-tab Evidence +
			// Investigation Steps caches now so the tab count badges are ready
			// before the user clicks those tabs. Fire-and-forget — never blocks
			// the landing. SKU-off cases skip these (the endpoints 404 there).
			if (data?.investigation != null) {
				queryClient.prefetchQuery(caseQueries.case_evidence(tenant, id))
				queryClient.prefetchQuery(caseQueries.investigation_history(tenant, id))
			}

			return data
		}
	},

	head: ({ loaderData }) => ({
		meta: [
			{
				title: `${loaderData?.case.alert_name ? loaderData?.case.alert_name : 'Case Details'} | Case Management | BitLyft AIR®`,
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: CaseDetailsSkeleton,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const { id } = Route.useParams()
	const pageContext = useCasePageContext()

	return (
		<AIRailLayout pageContext={pageContext}>
			<Section>
				<div className="bg-background flex flex-col gap-4 pt-2 pb-3">
					<ErrorBoundary
						feature="case-management"
						section="title"
						entityId={id}
					>
						<Suspense fallback={<DetailsTitleSkeleton />}>
							<DetailsTitle id={id} />
						</Suspense>
					</ErrorBoundary>

					<ErrorBoundary
						feature="case-management"
						section="header-cluster"
						entityId={id}
					>
						<Suspense fallback={<DetailsHeaderClusterSkeleton />}>
							<DetailsHeaderCluster id={id} />
						</Suspense>
					</ErrorBoundary>

					<ErrorBoundary feature="case-management" section="tabs" entityId={id}>
						<Suspense fallback={<DetailsTabsSkeleton />}>
							<DetailsTabs id={id} />
						</Suspense>
					</ErrorBoundary>
				</div>

				<Outlet />

				<ErrorBoundary
					feature="case-management"
					section="log-panel-mount"
					entityId={id}
				>
					<Suspense fallback={null}>
						<LogPanelMount id={id} />
					</Suspense>
				</ErrorBoundary>
			</Section>
		</AIRailLayout>
	)
}
